// src\Pages\Home.jsx
import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import * as THREE from 'three';
import {
  motion,
  useAnimation,
  useInView,
  AnimatePresence,
} from 'framer-motion';
import {
  FiPenTool,
  FiGrid,
  FiLayers,
  FiMonitor,
  FiSmartphone,
  FiEye,
  FiStar,
  FiZap,
  FiArrowRight,
  FiCheckCircle,
  FiBriefcase,
  FiUsers,
  FiHeart,
  FiAward,
  FiTrendingUp,
  FiClock,
  FiMessageCircle,
  FiPlay,
  FiGithub,
  FiLinkedin,
  FiTwitter,
  FiInstagram,
  FiMail,
  FiMapPin,
  FiPhone,
  FiExternalLink,
  FiThumbsUp,
  FiShield,
  FiTarget,
  FiGlobe,
  FiCode,
  FiDatabase,
  FiCloud,
  FiTool,
  FiBox,
  FiSliders,
  FiDroplet,
  FiSun,
  FiMoon,
  FiCompass,
  FiHexagon,
  FiImage,
  FiPackage,
  FiBookOpen,
  FiRefreshCw,
  FiBarChart2,
  FiTrendingUp as FiTrendingUpIcon,
  FiFacebook,
} from 'react-icons/fi';
import { FaBehance } from 'react-icons/fa';
import { TfiPinterest } from 'react-icons/tfi';
import { HiOutlineLightBulb } from 'react-icons/hi';
import { TfiRocket, TfiBrush } from 'react-icons/tfi';
import { usePortfolio } from '../hooks/usePortfolio';
import CursorParticles from '../components/CursorParticles';
import {
  db,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from '../services/firebase';
import ReviewsSection from '../components/ReviewsSection';
import StatsSection from '../components/StatsSection';
import { useTheme } from '../contexts/ThemeContext';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const scaleUp = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: 'easeOut' },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

// Social Links - Matching Footer
const socialLinks = [
  {
    name: 'Facebook',
    icon: FiFacebook,
    href: 'https://www.facebook.com/wdhejbul',
    color: '#1877F2',
  },
  {
    name: 'Instagram',
    icon: FiInstagram,
    href: 'https://www.instagram.com/wdhejbul/',
    color: '#E4405F',
  },
  {
    name: 'LinkedIn',
    icon: FiLinkedin,
    href: 'https://www.linkedin.com/in/hejbul-rahman-tasin-127350265',
    color: '#0A66C2',
  },
  {
    name: 'Twitter',
    icon: FiTwitter,
    href: 'https://x.com/wdhejbul',
    color: '#1DA1F2',
  },
  {
    name: 'Pinterest',
    icon: TfiPinterest,
    href: 'https://www.pinterest.com/wdhejbul',
    color: '#E60023',
  },
  {
    name: 'Behance',
    icon: FaBehance,
    href: 'https://www.behance.net/wdhejbul',
    color: '#1769FF',
  },
  {
    name: 'GitHub',
    icon: FiGithub,
    href: 'https://github.com/wdhejbul',
    color: '#6e5494',
  },
];

const tools = [
  {
    name: 'Adobe Photoshop',
    imageUrl:
      'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1781857440/Photoshop_icon_png_crcjdr.png',
    category: 'Design',
    proficiency: 95,
    usage: 90,
    description: 'Image editing, photo manipulation, and digital art',
  },
  {
    name: 'Adobe Illustrator',
    imageUrl:
      'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1781857439/Illustrator_icon_png_pf140c.png',
    category: 'Design',
    proficiency: 90,
    usage: 85,
    description: 'Vector graphics, logo design, and illustration',
  },
  {
    name: 'Figma',
    imageUrl:
      'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1781857440/Figma_icon_png_g40oge.png',
    category: 'Design',
    proficiency: 75,
    usage: 65,
    description:
      'Primary design tool for UI/UX, prototyping, and collaboration',
  },
  {
    name: 'Adobe After Effects',
    imageUrl:
      'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1781857439/Adobe_After_Effect_icon_png_y7zy3m.png',
    category: 'Motion',
    proficiency: 78,
    usage: 40,
    description: 'Motion graphics, animation, and visual effects',
  },
];

// Services I Provide - Replace the tools array with this or keep both
const services = [
  {
    name: 'Brand Identity',
    icon: TfiBrush,
    category: 'Branding',
    proficiency: 98,
    demand: 95,
    description: 'Complete brand identity design and strategy',
  },
  {
    name: 'Product Design',
    icon: TfiRocket,
    category: 'Design',
    proficiency: 92,
    demand: 88,
    description: 'User-centered product design and prototyping',
  },
  {
    name: 'Image Tracing',
    icon: FiEye,
    category: 'Illustration',
    proficiency: 90,
    demand: 75,
    description: 'Professional image tracing and vectorization',
  },
  {
    name: 'Others',
    icon: FiMonitor,
    category: 'universal',
    proficiency: 88,
    demand: 85,
    description:
      'I provide various other design services tailored to your needs',
  },
];

// =============================================
// GLASSY CARD
// =============================================
const GlassyCard = ({ children, className = '' }) => {
  return (
    <div
      className={`relative overflow-hidden backdrop-blur-xl ${className}`}
      style={{
        backgroundColor: 'rgba(var(--bg-rgb), 0.3)',
        border: '1px solid rgba(var(--text-rgb), 0.06)',
        borderRadius: '16px',
        boxShadow:
          '0 4px 24px rgba(0, 0, 0, 0.02), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
      }}
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-br from-white/3 via-transparent to-transparent" />
        <div className="absolute -inset-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-[shimmer_8s_infinite]" />
      </div>
      {children}
    </div>
  );
};

// =============================================
// GLASSY DIVIDER
// =============================================
const GlassyDivider = () => {
  return (
    <div className="relative py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="relative h-px w-full overflow-hidden">
          <motion.div
            animate={{
              x: ['-100%', '100%'],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute inset-0 w-1/3"
            style={{
              background:
                'linear-gradient(to right, transparent, var(--text), transparent)',
              opacity: 0.08,
            }}
          />
        </div>
      </div>
    </div>
  );
};

// =============================================
// LIQUID GRADIENT HERO
// =============================================
const HomeHero = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden">
      <CursorParticles />

      {/* Liquid gradient background - Theme aware */}
      <div className="absolute inset-0">
        <motion.div
          animate={{
            x: ['-10%', '10%', '-10%'],
            y: ['-10%', '15%', '-10%'],
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(0,0,0,0.06) 0%, transparent 70%)',
            opacity: isDark ? 0.06 : 0.08,
          }}
        />
        <motion.div
          animate={{
            x: ['15%', '-10%', '15%'],
            y: ['15%', '-10%', '15%'],
            scale: [1.2, 1, 1.2],
          }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -bottom-40 -right-20 w-96 h-96 rounded-full blur-3xl"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(0,0,0,0.04) 0%, transparent 70%)',
            opacity: isDark ? 0.05 : 0.06,
          }}
        />
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[120px]"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(255,255,255,0.04) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(0,0,0,0.03) 0%, transparent 70%)',
            opacity: isDark ? 0.06 : 0.08,
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative z-10 max-w-5xl mx-auto text-center"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-sm backdrop-blur-sm"
          style={{
            backgroundColor: isDark
              ? 'rgba(0,0,0,0.3)'
              : 'rgba(255,255,255,0.5)',
            border: isDark
              ? '1px solid rgba(255,255,255,0.06)'
              : '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <FiZap className="w-4 h-4" style={{ color: 'var(--text)' }} />
          <span style={{ color: 'var(--muted)' }}>
            Brand & Product Designer
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight tracking-tight mb-6"
          style={{ color: 'var(--text)' }}
        >
          I design clean, impactful
          <br className="hidden sm:block" />
          <span
            className="relative inline-block mt-2"
            style={{ color: 'var(--muted)' }}
          >
            visual experiences
            <motion.span
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ delay: 0.8, duration: 0.8, ease: 'easeOut' }}
              className="absolute -bottom-3 left-0 h-0.5 rounded-full"
              style={{ backgroundColor: 'var(--text)', opacity: 0.15 }}
            />
          </span>{' '}
          for brands
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-base md:text-lg max-w-2xl mx-auto mb-10"
          style={{ color: 'var(--muted)', opacity: 0.8 }}
        >
          Helping brands stand out with strategic design, product visuals, and
          modern digital experiences.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          {/* Primary Button - Theme aware with smooth gradient */}
          <motion.a
            href="/portfolio"
            className="group relative inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium overflow-hidden transition-all duration-300 ease-out"
            style={{ color: isDark ? '#ffffff' : '#000000' }}
            whileHover={{
              scale: 1.03,
              transition: { duration: 0.25, ease: 'easeOut' },
            }}
            whileTap={{
              scale: 0.97,
              transition: { duration: 0.15, ease: 'easeOut' },
            }}
          >
            {/* Subtle gradient background - Theme aware */}
            <motion.span
              className="absolute inset-0 rounded-full"
              style={{
                background: isDark
                  ? 'linear-gradient(135deg, #1a1a1a, #2d2d2d, #1a1a1a)'
                  : 'linear-gradient(135deg, #e0e0e0, #f5f5f5, #e0e0e0)',
                backgroundSize: '200% 200%',
              }}
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            {/* Subtle shine on hover - Theme aware */}
            <motion.span
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: isDark
                  ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)'
                  : 'linear-gradient(90deg, transparent, rgba(0,0,0,0.06), transparent)',
                transform: 'translateX(-100%)',
              }}
              animate={{
                transform: ['translateX(-100%)', 'translateX(100%)'],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            <span className="relative z-10 flex items-center gap-2 font-medium tracking-wide">
              View Portfolio
              <motion.span
                className="inline-block"
                animate={{
                  x: [0, 0],
                }}
                transition={{
                  duration: 0.2,
                }}
              >
                <FiArrowRight className="w-4 h-4 transition-all duration-300 group-hover:translate-x-0.5 group-hover:scale-110" />
              </motion.span>
            </span>
          </motion.a>

          {/* Secondary Button - Theme aware outline */}
          <motion.a
            href="/hire"
            className="relative inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-300 ease-out hover:scale-103"
            style={{
              border: isDark
                ? '1px solid rgba(255,255,255,0.15)'
                : '1px solid rgba(0,0,0,0.12)',
              color: 'var(--text)',
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.03)'
                : 'rgba(0,0,0,0.02)',
              backdropFilter: 'blur(8px)',
            }}
            whileHover={{
              scale: 1.03,
              borderColor: isDark
                ? 'rgba(255,255,255,0.3)'
                : 'rgba(0,0,0,0.25)',
              backgroundColor: isDark
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.04)',
              transition: { duration: 0.25, ease: 'easeOut' },
            }}
            whileTap={{
              scale: 0.97,
              transition: { duration: 0.15, ease: 'easeOut' },
            }}
          >
            {/* Subtle glow on hover - Theme aware */}
            <motion.span
              className="absolute inset-0 rounded-full opacity-0"
              style={{
                background: isDark
                  ? 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.04), transparent 70%)'
                  : 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.04), transparent 70%)',
              }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            />
            <span className="relative z-10 font-medium tracking-wide">
              Hire Me
            </span>
          </motion.a>
        </motion.div>

        {/* Decorative line */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="mt-10 flex items-center justify-center gap-3"
        >
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
          <span
            className="text-xs tracking-widest uppercase"
            style={{ color: 'var(--muted)', opacity: 0.3 }}
          >
            Explore
          </span>
          <span
            className="w-12 h-px"
            style={{ backgroundColor: 'var(--border)' }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

// =============================================
// FEATURED PROJECTS - Glassmorphism Style with Mobile Click Support
// =============================================
const FeaturedProjects = ({ projects, loading }) => {
  const featuredProjects = projects?.filter(p => p.featured).slice(0, 6) || [];

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div
          className="w-12 h-12 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--text)' }}
        />
      </div>
    );
  }

  if (featuredProjects.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-lg" style={{ color: 'var(--muted)' }}>
          No featured projects yet. Check back soon!
        </p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {featuredProjects.map((project, index) => {
        const getThumbnailUrl = () => {
          if (project.thumbnailUrl) return project.thumbnailUrl;
          if (project.images && project.images.length > 0)
            return project.images[0].url;
          return (
            project.imageUrl ||
            'https://placehold.co/600x400/333/white?text=No+Image'
          );
        };

        const thumbnailUrl = getThumbnailUrl();
        const imageCount = project.images?.length || (project.imageUrl ? 1 : 0);

        return (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.08, duration: 0.5 }}
            viewport={{ once: true }}
            className="group"
          >
            {/* FIX: Make the entire card clickable */}
            <NavLink
              to={`/portfolio/${project.id}`}
              className="block"
              onClick={e => {
                // Prevent default if there's any issue
                e.stopPropagation();
              }}
            >
              <GlassyCard className="rounded-2xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl cursor-pointer">
                <div className="aspect-[4/3] overflow-hidden relative">
                  {thumbnailUrl ? (
                    <img
                      src={thumbnailUrl}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <FiImage
                        className="w-12 h-12"
                        style={{ color: 'var(--muted)' }}
                      />
                    </div>
                  )}

                  {/* Image Count Badge */}
                  {imageCount > 1 && (
                    <div
                      className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-md backdrop-blur-md text-xs z-10"
                      style={{
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <FiImage className="w-3 h-3" />
                      <span>{imageCount}</span>
                    </div>
                  )}

                  {/* Type Badge */}
                  {project.type && (
                    <div
                      className="absolute top-3 left-3 px-2 py-0.5 rounded-md text-xs font-medium backdrop-blur-md z-10"
                      style={{
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      {project.type === 'random' ? '🎲 Random' : '📁 Project'}
                    </div>
                  )}

                  {/* Featured Badge */}
                  {project.featured && (
                    <div
                      className="absolute top-3 left-3 mt-7 px-2 py-0.5 rounded-md text-xs font-medium backdrop-blur-md z-10 flex items-center gap-1"
                      style={{
                        backgroundColor: 'rgba(255,215,0,0.3)',
                        color: 'white',
                        border: '1px solid rgba(255,215,0,0.2)',
                      }}
                    >
                      <FiStar className="w-3 h-3" /> Featured
                    </div>
                  )}

                  {/* Hover Overlay - Always visible on mobile with tap feedback */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity duration-500 flex items-end">
                    <div className="p-6 text-white w-full">
                      <h3 className="text-xl font-bold mb-1 line-clamp-1">
                        {project.title}
                      </h3>
                      <p className="text-sm text-white/80 mb-3 line-clamp-2">
                        {project.description?.substring(0, 100)}
                        {project.description?.length > 100 ? '...' : ''}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-white/60">
                        <span className="capitalize">{project.category}</span>
                        <span>•</span>
                        <span>
                          {project.createdAt
                            ? new Date(project.createdAt).getFullYear()
                            : 'N/A'}
                        </span>
                        {imageCount > 1 && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <FiImage className="w-3 h-3" />
                              {imageCount}
                            </span>
                          </>
                        )}
                      </div>
                      {/* FIX: Always show the "View Project" link on mobile */}
                      <div className="inline-flex items-center gap-2 text-sm group-hover:gap-3 transition-all mt-3 text-white hover:text-white/80">
                        View Project <FiArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  {/* FIX: Mobile touch indicator - small arrow at bottom */}
                  <div className="absolute bottom-3 right-3 md:hidden flex items-center gap-1 text-xs text-white/50 bg-black/30 backdrop-blur-sm px-2 py-1 rounded-full">
                    <span>Tap</span>
                    <FiArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </GlassyCard>
            </NavLink>
          </motion.div>
        );
      })}
    </div>
  );
};

// Modern Process Section
function ProcessSection() {
  const processSteps = [
    {
      step: '01',
      title: 'Discovery',
      description: 'Deep dive into your brand, goals, and audience.',
      icon: FiTarget,
    },
    {
      step: '02',
      title: 'Strategy',
      description: 'Market analysis and user insights to inform design.',
      icon: FiEye,
    },
    {
      step: '03',
      title: 'Creation',
      description: 'Creative exploration and initial concepts.',
      icon: FiPenTool,
    },
    {
      step: '04',
      title: 'Delivery',
      description: 'Final assets with comprehensive documentation.',
      icon: FiCheckCircle,
    },
  ];

  return (
    <section className="py-24 px-4 max-w-7xl mx-auto overflow-hidden">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={staggerContainer}
        className="text-center mb-16"
      >
        <motion.span
          variants={fadeInUp}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <FiCompass className="w-4 h-4" />
          My Process
        </motion.span>
        <motion.h2
          variants={fadeInUp}
          className="text-3xl md:text-4xl font-bold mb-4"
        >
          How I <span className="italic">work</span>
        </motion.h2>
        <motion.p
          variants={fadeInUp}
          className="text-lg max-w-2xl mx-auto"
          style={{ color: 'var(--muted)' }}
        >
          A streamlined approach to deliver exceptional results
        </motion.p>
      </motion.div>

      <div className="relative">
        <div
          className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px hidden md:block"
          style={{ backgroundColor: 'var(--border)' }}
        />

        <div className="space-y-12">
          {processSteps.map((step, index) => (
            <motion.div
              key={step.step}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={index % 2 === 0 ? fadeInLeft : fadeInRight}
              transition={{ delay: index * 0.1 }}
              className={`relative flex flex-col md:flex-row gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
            >
              <div
                className="absolute left-8 md:left-1/2 top-0 -translate-x-1/2 w-4 h-4 rounded-full hidden md:block"
                style={{ backgroundColor: 'var(--text)' }}
              />

              <div
                className={`flex-1 ${index % 2 === 0 ? 'md:text-right md:pr-12' : 'md:pl-12'}`}
              >
                <div
                  className="inline-block p-6 rounded-2xl w-full md:w-auto"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-bold"
                      style={{
                        backgroundColor: 'var(--bg)',
                        color: 'var(--text)',
                      }}
                    >
                      {step.step}
                    </div>
                    <h3 className="text-xl font-bold">{step.title}</h3>
                  </div>
                  <p className="text-sm" style={{ color: 'var(--muted)' }}>
                    {step.description}
                  </p>
                  <step.icon className="w-5 h-5 mt-4 opacity-50" />
                </div>
              </div>

              <div className="flex-1 hidden md:block" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================
// SERVICES SECTION - Enhanced with Soft Borders & Slide Overlay
// =============================================
const ServicesSection = ({ services, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 rounded-full border-2 border-t-transparent"
          style={{ borderColor: 'var(--text)' }}
        />
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="text-center py-20"
      >
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: 'var(--border)' }}
        >
          <FiPackage className="w-10 h-10" style={{ color: 'var(--muted)' }} />
        </div>
        <p className="text-lg" style={{ color: 'var(--muted)' }}>
          No services available yet. Check back soon!
        </p>
      </motion.div>
    );
  }

  return (
    <>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.slice(0, 3).map((service, index) => {
          const [isHovered, setIsHovered] = useState(false);
          const cardRef = useRef(null);

          return (
            <motion.div
              key={service.id}
              ref={cardRef}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                delay: index * 0.1,
                duration: 0.6,
                type: 'spring',
                stiffness: 100,
                damping: 15,
              }}
              viewport={{ once: true, margin: '-50px' }}
              className="group"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <div
                className="relative rounded-2xl overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl h-full flex flex-col"
                style={{
                  backgroundColor: 'var(--bg)',
                  border: '1px solid var(--border)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                }}
              >
                {/* Soft border glow on hover */}
                <motion.div
                  className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{
                    background:
                      'linear-gradient(135deg, var(--text) 0%, transparent 70%)',
                    opacity: 0.04,
                  }}
                />

                {/* Slide Overlay Effect - From Left */}
                <motion.div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(135deg, var(--text) 0%, transparent 70%)',
                    opacity: 0.03,
                  }}
                  initial={{ x: '-100%' }}
                  animate={{ x: isHovered ? '0%' : '-100%' }}
                  transition={{ duration: 0.5, ease: 'easeInOut' }}
                />

                {/* Popular Badge with animation */}
                {service.popular && (
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                    viewport={{ once: true }}
                    className="absolute top-0 left-0 z-20 px-3 py-1 rounded-br-lg text-xs font-medium flex items-center gap-1"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    >
                      <FiStar className="w-3 h-3" />
                    </motion.div>
                    Most Popular
                  </motion.div>
                )}

                {/* Image Container with Zoom Effect */}
                <div className="relative w-full bg-black/5 dark:bg-white/5 flex-shrink-0 overflow-hidden">
                  <div className="relative w-full pt-[56.25%]">
                    {service.imageUrl ? (
                      <>
                        <motion.img
                          src={service.imageUrl}
                          alt={service.name}
                          className="absolute top-0 left-0 w-full h-full object-cover"
                          initial={{ scale: 1 }}
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.7 }}
                        />
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"
                          initial={{ opacity: 0 }}
                          whileHover={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                        />
                      </>
                    ) : (
                      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                        <FiPackage
                          className="w-12 h-12"
                          style={{ color: 'var(--muted)' }}
                        />
                      </div>
                    )}

                    {/* Animated overlay on hover */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col p-5 relative z-10">
                  <motion.h3
                    className="text-xl font-bold mb-2 line-clamp-1"
                    style={{ color: 'var(--text)' }}
                    whileHover={{ x: 4 }}
                  >
                    {service.name}
                  </motion.h3>

                  <motion.p
                    className="text-sm mb-4 line-clamp-2"
                    style={{ color: 'var(--muted)' }}
                    whileHover={{ x: 4 }}
                  >
                    {service.description}
                  </motion.p>

                  {/* Features with animated checkmarks */}
                  <div className="flex-1">
                    <ul className="space-y-2 mb-4">
                      {service.features?.slice(0, 2).map((feature, idx) => (
                        <motion.li
                          key={idx}
                          className="flex items-center gap-2 text-sm"
                          style={{ color: 'var(--muted)' }}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 + idx * 0.05 + 0.2 }}
                          viewport={{ once: true }}
                        >
                          <motion.div
                            whileHover={{ scale: 1.2, rotate: 10 }}
                            className="flex-shrink-0"
                          >
                            <FiCheckCircle
                              className="w-4 h-4"
                              style={{ color: 'var(--text)' }}
                            />
                          </motion.div>
                          <span className="truncate">{feature}</span>
                        </motion.li>
                      ))}
                      {service.features?.length > 2 && (
                        <motion.li
                          className="text-xs ml-6"
                          style={{ color: 'var(--muted)' }}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          transition={{ delay: index * 0.1 + 0.3 }}
                          viewport={{ once: true }}
                        >
                          +{service.features.length - 2} more features
                        </motion.li>
                      )}
                    </ul>
                  </div>

                  {/* Price & Duration with Animation */}
                  <motion.div
                    className="flex items-center justify-between pt-3 mt-auto border-t"
                    style={{ borderColor: 'var(--border)' }}
                    whileHover={{ y: -2 }}
                  >
                    <motion.div
                      className="flex items-baseline gap-1"
                      whileHover={{ scale: 1.05 }}
                    >
                      <span
                        className="text-2xl font-bold"
                        style={{ color: 'var(--text)' }}
                      >
                        ${service.price}
                      </span>
                      <span
                        className="text-xs"
                        style={{ color: 'var(--muted)' }}
                      >
                        /project
                      </span>
                    </motion.div>
                    <div
                      className="flex items-center gap-1 text-xs"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiClock className="w-3 h-3" />
                      <span>{service.duration}</span>
                    </div>
                  </motion.div>

                  {/* CTA Button with Slide Overlay Effect */}
                  <motion.div
                    className="mt-4 relative overflow-hidden rounded-lg"
                    whileHover={{ scale: 1.02 }}
                  >
                    {/* Slide overlay background */}
                    <motion.div
                      className="absolute inset-0 rounded-lg"
                      style={{
                        background:
                          'linear-gradient(135deg, var(--text) 0%, var(--text) 100%)',
                        opacity: 0.06,
                      }}
                      initial={{ x: '-100%' }}
                      animate={{ x: isHovered ? '0%' : '-100%' }}
                      transition={{ duration: 0.4, ease: 'easeInOut' }}
                    />

                    <NavLink
                      to="/services"
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 relative z-10"
                      style={{
                        border: '1px solid var(--border)',
                        color: 'var(--text)',
                        backgroundColor: 'transparent',
                      }}
                    >
                      <span>Learn More</span>
                      <motion.div
                        animate={isHovered ? { x: 4 } : { x: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <FiArrowRight className="w-4 h-4" />
                      </motion.div>
                    </NavLink>
                  </motion.div>
                </div>

                {/* Corner accent with soft rotation */}
                <motion.div
                  className="absolute top-0 right-0 w-16 h-16 overflow-hidden pointer-events-none"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 + 0.2 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="absolute top-0 right-0 w-8 h-8 -translate-y-1/2 translate-x-1/2 rotate-45"
                    style={{
                      backgroundColor: 'var(--border)',
                      opacity: 0.3,
                    }}
                    whileHover={{ scale: 1.2, rotate: 90 }}
                    transition={{ duration: 0.4 }}
                  />
                </motion.div>

                {/* Bottom soft border glow */}
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-px"
                  style={{
                    background:
                      'linear-gradient(to right, transparent, var(--text), transparent)',
                    opacity: 0.05,
                  }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
                  viewport={{ once: true }}
                />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* View All Services Button */}
      {services.length > 3 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="relative group"
          >
            <NavLink
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-300 hover:gap-3 relative overflow-hidden"
              style={{
                border: '1px solid var(--border)',
                color: 'var(--text)',
                backgroundColor: 'var(--bg)',
              }}
            >
              {/* Button slide overlay */}
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  background:
                    'linear-gradient(135deg, var(--text) 0%, transparent 70%)',
                  opacity: 0.04,
                }}
                initial={{ x: '-100%' }}
                whileHover={{ x: '0%' }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              />

              <span className="relative z-10">
                View All Services ({services.length})
              </span>
              <motion.div
                className="relative z-10"
                animate={{ x: [0, 4, 0] }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <FiArrowRight className="w-4 h-4" />
              </motion.div>
            </NavLink>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

// =============================================
// DESIGN PHILOSOPHY - Glassmorphism
// =============================================
const DesignPhilosophySection = () => {
  const philosophies = [
    {
      icon: HiOutlineLightBulb,
      title: 'Human-Centered',
      description:
        'Every design starts with understanding real people and their needs.',
      gradient: 'from-yellow-500/20 to-yellow-600/10',
    },
    {
      icon: FiRefreshCw,
      title: 'Iterative Excellence',
      description: 'Continuous refinement through feedback and testing.',
      gradient: 'from-blue-500/20 to-blue-600/10',
    },
    {
      icon: TfiRocket,
      title: 'Impact-Driven',
      description: 'Designs that drive measurable business outcomes.',
      gradient: 'from-purple-500/20 to-purple-600/10',
    },
    {
      icon: FiHeart,
      title: 'Emotion & Story',
      description:
        'Creating meaningful connections through visual storytelling.',
      gradient: 'from-pink-500/20 to-pink-600/10',
    },
  ];

  return (
    <section
      className="py-24 px-4 overflow-hidden"
      style={{ backgroundColor: 'var(--border)' }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ backgroundColor: 'var(--bg)' }}
          >
            <FiBookOpen className="w-4 h-4" />
            <span className="text-sm" style={{ color: 'var(--text)' }}>
              Design Philosophy
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: 'var(--text)' }}
          >
            My{' '}
            <span className="italic" style={{ color: 'var(--muted)' }}>
              design principles
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg max-w-2xl mx-auto"
            style={{ color: 'var(--muted)' }}
          >
            Guiding principles that shape every project I undertake
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {philosophies.map((item, index) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="group"
            >
              <GlassyCard className="p-8 text-center rounded-2xl transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl">
                <motion.div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, ${item.gradient}, transparent 70%)`,
                  }}
                />

                <div className="relative z-10">
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{ backgroundColor: 'var(--bg)' }}
                  >
                    <item.icon
                      className="w-8 h-8"
                      style={{ color: 'var(--text)' }}
                    />
                  </div>
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: 'var(--text)' }}
                  >
                    {item.title}
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: 'var(--muted)' }}
                  >
                    {item.description}
                  </p>
                  {/* Animated dot */}
                  <motion.div
                    className="w-1.5 h-1.5 rounded-full mx-auto mt-4"
                    style={{ backgroundColor: 'var(--text)', opacity: 0.2 }}
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [0.2, 0.5, 0.2],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.2,
                    }}
                  />
                </div>
              </GlassyCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// =============================================
// VIDEO SHOWREEL - Glassmorphism
// =============================================
const VideoShowreel = ({ isOpen, onOpen, onClose }) => {
  return (
    <>
      <section
        className="py-24 px-4 overflow-hidden"
        style={{ backgroundColor: 'var(--border)' }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: 'var(--bg)' }}
            >
              <FiPlay className="w-4 h-4" />
              <span className="text-sm" style={{ color: 'var(--text)' }}>
                Showreel
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Watch my{' '}
              <span className="italic" style={{ color: 'var(--muted)' }}>
                work
              </span>{' '}
              in action
            </motion.h2>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="group relative cursor-pointer rounded-2xl overflow-hidden"
            onClick={onOpen}
          >
            <GlassyCard className="aspect-video relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1536240474400-b2b87b1e2a5b?w=1200"
                alt="Video thumbnail"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center transition-all duration-300 group-hover:bg-black/40">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300"
                >
                  <FiPlay className="w-8 h-8 text-white ml-1" />
                </motion.div>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h3 className="text-white text-xl font-bold">
                  2024 Design Showreel
                </h3>
                <p className="text-white/80 text-sm">
                  A collection of my best works
                </p>
              </div>
            </GlassyCard>
          </motion.div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-4xl"
              onClick={e => e.stopPropagation()}
            >
              <button
                onClick={onClose}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
              >
                Close ✕
              </button>
              <GlassyCard className="aspect-video rounded-2xl overflow-hidden flex items-center justify-center bg-black">
                <p className="text-white/50">
                  Video player would be embedded here
                </p>
              </GlassyCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// =============================================
// CTA SECTION - Glassmorphism
// =============================================
const CTASection = () => {
  return (
    <section className="py-24 px-4 max-w-7xl mx-auto overflow-hidden">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <GlassyCard className="relative rounded-3xl p-12 text-center overflow-hidden">
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(circle, var(--text) 0%, transparent 70%)',
              opacity: 0.03,
            }}
          />

          <div className="relative z-10">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Ready to start your{' '}
              <span className="italic" style={{ color: 'var(--muted)' }}>
                project?
              </span>
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{ color: 'var(--muted)' }}
            >
              Let's collaborate and create something extraordinary together.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.a
                href="/hire"
                whileHover={{ scale: 1.03, gap: '0.75rem' }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                Hire Me <FiArrowRight className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="/contact"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Contact Me <FiMail className="w-5 h-5" />
              </motion.a>
            </div>
          </div>
        </GlassyCard>
      </motion.div>
    </section>
  );
};

// =============================================
// MAIN HOME COMPONENT
// =============================================
function Home() {
  const { projects, loading } = usePortfolio();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [servicesFromFirebase, setServicesFromFirebase] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoadingServices(true);
        const q = query(
          collection(db, 'readyMadeServices'),
          where('status', '==', 'active'),
          orderBy('popular', 'desc'),
          orderBy('order', 'asc'),
        );
        const snapshot = await getDocs(q);
        const servicesList = [];
        snapshot.forEach(doc => {
          servicesList.push({ id: doc.id, ...doc.data() });
        });
        setServicesFromFirebase(servicesList);
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
    >
      <HomeHero />

      <StatsSection
        fetchProjectCount={true}
        columns={4}
        variant="default"
        showBorder={true}
      />

      <GlassyDivider />

      {/* Featured Portfolio Section */}
      <section className="py-24 px-4 max-w-7xl mx-auto overflow-hidden">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <FiStar className="w-4 h-4" />
            <span className="text-sm" style={{ color: 'var(--text)' }}>
              Featured Work
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: 'var(--text)' }}
          >
            Selected{' '}
            <span className="italic" style={{ color: 'var(--muted)' }}>
              projects
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-lg max-w-2xl mx-auto"
            style={{ color: 'var(--muted)' }}
          >
            A showcase of my best work
          </motion.p>
        </div>

        <FeaturedProjects projects={projects} loading={loading} />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <NavLink
            to="/portfolio"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all hover:gap-3"
            style={{ border: '1px solid var(--border)', color: 'var(--text)' }}
          >
            View All Projects <FiArrowRight className="w-4 h-4" />
          </NavLink>
        </motion.div>
      </section>

      <GlassyDivider />

      {/* Tools & Technologies - Keep as is */}
      <section
        className="py-24 px-4 overflow-hidden"
        style={{ backgroundColor: 'var(--border)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6"
              style={{ backgroundColor: 'var(--bg)' }}
            >
              <FiTool className="w-4 h-4" /> My Toolkit
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Tools &{' '}
              <span className="italic" style={{ color: 'var(--muted)' }}>
                Technologies
              </span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'var(--muted)' }}
            >
              Industry-leading tools I use to bring ideas to life
            </motion.p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {tools.map((tool, index) => (
              <motion.div
                key={tool.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={scaleUp}
                transition={{ delay: index * 0.1 }}
                className="group relative rounded-2xl p-6 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
                style={{
                  backgroundColor: 'var(--bg)',
                  border: '1px solid var(--border)',
                }}
              >
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-black/5 via-transparent to-black/5 dark:from-white/5 dark:via-transparent dark:to-white/5" />
                </div>
                <div className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl bg-black/5 dark:bg-white/5 pointer-events-none" />

                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                      <svg className="w-[88px] h-[88px] -rotate-90">
                        <rect
                          x="4"
                          y="4"
                          width="80"
                          height="80"
                          rx="12"
                          stroke="var(--border)"
                          strokeWidth="3"
                          fill="none"
                        />
                        <rect
                          x="4"
                          y="4"
                          width="80"
                          height="80"
                          rx="12"
                          stroke={isDark ? '#ffffff' : '#000000'}
                          strokeWidth="3"
                          fill="none"
                          strokeDasharray={`${(tool.proficiency / 100) * 304} 304`}
                          strokeLinecap="round"
                          className="transition-all duration-1000 ease-out"
                          style={{
                            strokeDashoffset: '0',
                          }}
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className="w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-500 group-hover:scale-110"
                          style={{ backgroundColor: 'var(--border)' }}
                        >
                          <img
                            src={tool.imageUrl}
                            alt={tool.name}
                            className="w-10 h-10 object-contain"
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <h3
                    className="text-lg font-bold text-center mb-1"
                    style={{ color: 'var(--text)' }}
                  >
                    {tool.name}
                  </h3>

                  <div className="flex justify-center mb-3">
                    <span
                      className="text-xs px-3 py-0.5 rounded-full"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      {tool.category}
                    </span>
                  </div>

                  <p
                    className="text-xs text-center mb-4 leading-relaxed"
                    style={{ color: 'var(--muted)' }}
                  >
                    {tool.description}
                  </p>

                  <div className="space-y-2.5">
                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span style={{ color: 'var(--text)' }}>
                          Proficiency
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: 'var(--text)' }}
                        >
                          {tool.proficiency}%
                        </span>
                      </div>
                      <div
                        className="h-1.5 rounded-full overflow-hidden"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${tool.proficiency}%` }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 1.2,
                            delay: 0.2 + index * 0.1,
                          }}
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            backgroundColor: 'var(--text)',
                            opacity: 0.9,
                          }}
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between text-xs mb-1">
                        <span style={{ color: 'var(--text)' }}>
                          Usage Frequency
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: 'var(--text)' }}
                        >
                          {tool.usage}%
                        </span>
                      </div>
                      <div
                        className="h-1.5 rounded-full overflow-hidden"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${tool.usage}%` }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 1.2,
                            delay: 0.4 + index * 0.1,
                          }}
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            backgroundColor: 'var(--text)',
                            opacity: 0.6,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <div
                      className="text-[10px] px-3 py-1 rounded-full flex items-center gap-1.5"
                      style={{
                        backgroundColor:
                          tool.usage >= 75 ? 'var(--text)' : 'var(--border)',
                        color: tool.usage >= 75 ? 'var(--bg)' : 'var(--text)',
                      }}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${tool.usage >= 75 ? 'bg-current' : 'bg-current opacity-50'}`}
                      />
                      {tool.usage >= 75 ? 'Daily Driver' : 'Frequently Used'}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <GlassyDivider />

      {/* Process Section */}
      <ProcessSection />

      <GlassyDivider />

      {/* Services Section - Glassmorphism with Advanced Animations */}
      <section
        className="py-24 px-4 overflow-hidden"
        style={{ backgroundColor: 'var(--border)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: 'var(--bg)' }}
            >
              <FiGrid className="w-4 h-4" style={{ color: '#3b82f6' }} />
              <span className="text-sm" style={{ color: 'var(--text)' }}>
                What I Do
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: 'var(--text)' }}
            >
              Services I{' '}
              <span className="italic" style={{ color: 'var(--muted)' }}>
                provide
              </span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'var(--muted)' }}
            >
              Comprehensive design solutions tailored to your business needs
            </motion.p>

            {/* Animated decorative line */}
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: '60px' }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="h-0.5 mx-auto mt-4 rounded-full"
              style={{ backgroundColor: 'var(--text)', opacity: 0.15 }}
            />
          </div>

          <ServicesSection
            services={servicesFromFirebase}
            loading={loadingServices}
          />
        </div>
      </section>

      <GlassyDivider />

      {/* Design Philosophy */}
      <DesignPhilosophySection />

      <GlassyDivider />

      {/* Video Showreel */}
      <VideoShowreel
        isOpen={isVideoModalOpen}
        onOpen={() => setIsVideoModalOpen(true)}
        onClose={() => setIsVideoModalOpen(false)}
      />

      <GlassyDivider />

      {/* About Section - Enhanced with Services */}
      <section className="py-24 px-4 max-w-7xl mx-auto overflow-hidden">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm mb-6"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <FiHeart className="w-4 h-4" /> About Me
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
          >
            Creating designs that{' '}
            <span className="italic">make a difference</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg max-w-2xl mx-auto"
            style={{ color: 'var(--muted)' }}
          >
            I'm a passionate designer with over 5 years of experience helping
            brands tell their stories through compelling visual experiences.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - About Content */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInLeft}
          >
            {/* Services with Creative Dividers */}
            <div className="space-y-5">
              {services.map((service, index) => (
                <div key={service.name}>
                  {/* Service Item */}
                  <div className="group">
                    <div className="flex justify-between text-sm mb-1">
                      <span
                        className="flex items-center gap-2"
                        style={{ color: 'var(--text)' }}
                      >
                        <service.icon
                          className="w-4 h-4"
                          style={{ color: 'var(--text)' }}
                        />
                        {service.name}
                        <span
                          className="text-xs font-normal"
                          style={{ color: 'var(--muted)' }}
                        >
                          • {service.category}
                        </span>
                      </span>
                      <span
                        className="font-medium"
                        style={{ color: 'var(--text)' }}
                      >
                        {service.proficiency}%
                      </span>
                    </div>
                    <div
                      className="h-1.5 rounded-full overflow-hidden"
                      style={{ backgroundColor: 'var(--border)' }}
                    >
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${service.proficiency}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: 0.2 + index * 0.1 }}
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          backgroundColor: 'var(--text)',
                          opacity: 0.9,
                        }}
                      />
                    </div>

                    {/* Usage Frequency Bar - Shows how often I provide this service */}
                    <div className="mt-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span style={{ color: 'var(--text)' }}>
                          Client Demand
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: 'var(--text)' }}
                        >
                          {service.demand}%
                        </span>
                      </div>
                      <div
                        className="h-1.5 rounded-full overflow-hidden"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: `${service.demand}%` }}
                          viewport={{ once: true }}
                          transition={{
                            duration: 1.2,
                            delay: 0.4 + index * 0.1,
                          }}
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            backgroundColor: 'var(--text)',
                            opacity: 0.6,
                          }}
                        />
                      </div>
                    </div>

                    {/* Demand Badge */}
                    <div className="mt-2 flex justify-start">
                      <div
                        className="text-[10px] px-3 py-0.5 rounded-full flex items-center gap-1.5"
                        style={{
                          backgroundColor:
                            service.demand >= 75
                              ? 'var(--text)'
                              : 'var(--border)',
                          color:
                            service.demand >= 75 ? 'var(--bg)' : 'var(--text)',
                        }}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${service.demand >= 75 ? 'bg-current' : 'bg-current opacity-50'}`}
                        />
                        {service.demand >= 75
                          ? 'High Demand'
                          : 'Growing Demand'}
                      </div>
                    </div>
                  </div>

                  {/* Creative Divider - Only between items, not after the last one */}
                  {index < services.length - 1 && (
                    <div className="relative my-5">
                      {/* Gradient Line Divider */}
                      <div className="flex items-center gap-4">
                        <div
                          className="flex-1 h-px"
                          style={{
                            background: `linear-gradient(90deg, transparent, var(--border), transparent)`,
                          }}
                        />

                        {/* Decorative Dot */}
                        <div className="relative flex items-center justify-center">
                          <div
                            className="w-2 h-2 rounded-full transition-all duration-300 group-hover:scale-150"
                            style={{
                              backgroundColor: 'var(--text)',
                              opacity: 0.3,
                            }}
                          />
                          <div
                            className="absolute w-4 h-4 rounded-full transition-all duration-500 group-hover:scale-110"
                            style={{
                              border: '1px solid var(--border)',
                              opacity: 0.5,
                            }}
                          />
                        </div>

                        <div
                          className="flex-1 h-px"
                          style={{
                            background: `linear-gradient(90deg, var(--border), transparent)`,
                          }}
                        />
                      </div>

                      {/* Service Tip - Shows proficiency in a subtle way */}
                      <div className="flex justify-center mt-1">
                        <span
                          className="text-[8px] uppercase tracking-widest opacity-30"
                          style={{ color: 'var(--text)' }}
                        >
                          {service.proficiency}% Expertise
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* About Description */}
            <div className="mt-8 space-y-4">
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--muted)' }}
              >
                I'm a multidisciplinary designer with expertise across brand
                identity, product design, and visual storytelling. My work
                combines strategic thinking with creative excellence to deliver
                solutions that resonate with audiences and drive results.
              </p>
              <p
                className="text-sm leading-relaxed"
                style={{ color: 'var(--muted)' }}
              >
                From concept to completion, I bring a holistic perspective to
                every project, ensuring consistency and impact across all
                touchpoints.
              </p>
            </div>

            {/* Service Stats Summary */}
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              <div
                className="text-center p-3 rounded-xl transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: 'var(--bg)',
                  border: '1px solid var(--border)',
                }}
              >
                <div
                  className="text-lg font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  4+
                </div>
                <div className="text-[10px]" style={{ color: 'var(--muted)' }}>
                  Services
                </div>
              </div>
              <div
                className="text-center p-3 rounded-xl transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: 'var(--bg)',
                  border: '1px solid var(--border)',
                }}
              >
                <div
                  className="text-lg font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  92%
                </div>
                <div className="text-[10px]" style={{ color: 'var(--muted)' }}>
                  Avg Expertise
                </div>
              </div>
              <div
                className="text-center p-3 rounded-xl transition-all duration-300 hover:scale-105"
                style={{
                  backgroundColor: 'var(--bg)',
                  border: '1px solid var(--border)',
                }}
              >
                <div
                  className="text-lg font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  78%
                </div>
                <div className="text-[10px]" style={{ color: 'var(--muted)' }}>
                  Avg Demand
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 mt-6">
              <NavLink
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all hover:gap-3"
                style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
              >
                View All Services <FiArrowRight className="w-4 h-4" />
              </NavLink>
              <NavLink
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-all hover:gap-3"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                Get In Touch <FiMessageCircle className="w-4 h-4" />
              </NavLink>
            </div>
          </motion.div>

          {/* Right Column - Profile Card (Matching Tools Card Style) */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInRight}
          >
            <div
              className="group relative rounded-2xl p-8 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl"
              style={{
                backgroundColor: 'var(--bg)',
                border: '1px solid var(--border)',
              }}
            >
              {/* Hover Overlay - Matching Tools Section */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-black/5 via-transparent to-black/5 dark:from-white/5 dark:via-transparent dark:to-white/5" />
              </div>
              <div className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl bg-black/5 dark:bg-white/5 pointer-events-none" />

              <div className="relative z-10 text-center">
                {/* Profile Image with Ring */}
                <div className="relative flex items-center justify-center mb-4">
                  <div className="relative">
                    <svg className="w-[88px] h-[88px] -rotate-90">
                      <rect
                        x="4"
                        y="4"
                        width="80"
                        height="80"
                        rx="12"
                        stroke="var(--border)"
                        strokeWidth="3"
                        fill="none"
                      />
                      <rect
                        x="4"
                        y="4"
                        width="80"
                        height="80"
                        rx="12"
                        stroke={isDark ? '#ffffff' : '#000000'}
                        strokeWidth="3"
                        fill="none"
                        strokeDasharray="304 304"
                        strokeLinecap="round"
                        className="transition-all duration-1000 ease-out"
                        style={{
                          strokeDashoffset: '0',
                        }}
                      />
                    </svg>

                    {/* Profile Image */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div
                        className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden transition-all duration-500 group-hover:scale-110"
                        style={{
                          backgroundColor: 'var(--border)',
                        }}
                      >
                        <img
                          src="https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1779165055/WD_Hajbul_Professional_Logo_Design_Personal_Brand_Identity_kkj9ek.jpg"
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Name */}
                <h3
                  className="text-xl font-bold mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  HEJBUL RAHMAN TASIN
                </h3>

                {/* Title Badge */}
                <div className="flex justify-center mb-4">
                  <span
                    className="text-xs px-3 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                  >
                    Brand & Product Designer
                  </span>
                </div>

                {/* Service Tags */}
                <div className="flex flex-wrap justify-center gap-1.5 mb-4">
                  {services.map(service => (
                    <span
                      key={service.name}
                      className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{
                        backgroundColor: 'var(--border)',
                        color: 'var(--text)',
                      }}
                    >
                      {service.name}
                    </span>
                  ))}
                </div>

                {/* Description */}
                <p
                  className="text-xs text-center mb-4 leading-relaxed"
                  style={{ color: 'var(--muted)' }}
                >
                  Passionate about creating meaningful design experiences that
                  connect brands with their audiences.
                </p>

                {/* Social Links - Matching Footer Style */}
                <div className="flex flex-wrap justify-center gap-2">
                  {socialLinks.map((social, idx) => (
                    <motion.a
                      key={idx}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="relative group"
                      aria-label={social.name}
                      whileHover={{ y: -4 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <motion.div
                        className="relative p-2 rounded-lg transition-all duration-300 overflow-hidden"
                        style={{
                          backgroundColor: 'var(--bg)',
                          border: '1px solid var(--border)',
                        }}
                        whileHover={{
                          borderColor: social.color,
                          boxShadow: `0 4px 20px ${social.color}20`,
                        }}
                      >
                        {/* Hover gradient overlay */}
                        <motion.div
                          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                          style={{
                            background: `linear-gradient(135deg, ${social.color}10, transparent 70%)`,
                          }}
                        />

                        <social.icon
                          className="w-4 h-4 relative z-10 transition-all duration-300 group-hover:scale-110"
                          style={{ color: 'var(--text)' }}
                        />

                        {/* Animated dot indicator */}
                        <motion.div
                          className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                          style={{ backgroundColor: social.color }}
                          initial={{ scale: 0, opacity: 0 }}
                          whileHover={{ scale: 1, opacity: 1 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        />
                      </motion.div>
                    </motion.a>
                  ))}
                </div>

                {/* Stats - Using dynamic project count */}
                <div
                  className="mt-4 pt-4 border-t grid grid-cols-3 gap-2"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <div>
                    <div
                      className="text-sm font-bold"
                      style={{ color: 'var(--text)' }}
                    >
                      {projects?.length || 0}+
                    </div>
                    <div
                      className="text-[10px]"
                      style={{ color: 'var(--muted)' }}
                    >
                      Projects
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-sm font-bold"
                      style={{ color: 'var(--text)' }}
                    >
                      95%
                    </div>
                    <div
                      className="text-[10px]"
                      style={{ color: 'var(--muted)' }}
                    >
                      Satisfaction
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-sm font-bold"
                      style={{ color: 'var(--text)' }}
                    >
                      5+
                    </div>
                    <div
                      className="text-[10px]"
                      style={{ color: 'var(--muted)' }}
                    >
                      Years
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <ReviewsSection />

      <GlassyDivider />

      {/* CTA Section */}
      <CTASection />

      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%) rotate(12deg);
          }
          100% {
            transform: translateX(200%) rotate(12deg);
          }
        }
      `}</style>
    </div>
  );
}

export default Home;
